/**
 * Strip a hand's native digits — the rig half of "Replace fingers" (Allen,
 * 2026-09-29: "hand joints aren't being offered if they've been resolved — no
 * way to retry hands when we did them, or when Tripo messed them up").
 *
 * Both cases are ONE case on the rig: a reopened export's grafted fingers come
 * back as ordinary bones (the export writes `X_graft` as `X`), and a Tripo rig
 * preset ships its own; either way the hand already has digits, so the graft
 * has nothing to add to. Replacing them means removing them first.
 *
 * ONE responsibility: given a rig and a hand bone, name the hand's DIGIT
 * CHAINS — the hand's children whose subtree the rig's own mapping calls a
 * finger (`…Hand{Thumb,Index,Middle,Ring,Pinky}N`) — and return the rig
 * without them: their bones, their mapping entries, the mirror pairs that name
 * them. A child that is not a finger (a prop socket, a weapon bone, a wrist
 * twist) is kept. Nothing about weights: the skin half is the adapter's
 * `stripBonesFromSkin`, which folds these bones' flesh into the hand.
 *
 * REFUSES rather than guessing: a hand bone the rig lacks, or a hand with no
 * mapped digit chain (there is nothing to replace — the graft door is open).
 *
 * Depends on: ir/rig-descriptor, ir/errors. Depended on by: the run's hand
 * pass (`nativeDigits: 'replace'`), tests.
 */
import { KernelError } from '../ir/errors.js';
import { parseRigDescriptor, type RigDescriptor } from '../ir/rig-descriptor.js';

/** The ontology's digit slots: `LeftHandIndex1`, `RightHandThumb3`, … */
const DIGIT_ID = /^(Left|Right)Hand(Thumb|Index|Middle|Ring|Pinky)\d+$/;

export class StripDigitsRefusal extends KernelError {}

export interface StripDigitsResult {
  readonly rig: RigDescriptor;
  /** Every bone removed, the digit chains' roots first (the hand's direct children), then their descendants. */
  readonly removed: readonly string[];
  /** The hand's direct children that were digit roots. */
  readonly chains: readonly string[];
}

export function stripDigitsFromRig(rig: RigDescriptor, handBone: string): StripDigitsResult {
  if (!rig.bones.some((b) => b.name === handBone)) {
    throw new StripDigitsRefusal(`replace fingers refused: the rig has no bone "${handBone}"`);
  }
  const children = new Map<string, string[]>();
  for (const b of rig.bones) {
    if (b.parent === null) continue;
    const list = children.get(b.parent) ?? [];
    list.push(b.name);
    children.set(b.parent, list);
  }
  const subtree = (root: string): string[] => {
    const out: string[] = [];
    const stack = [root];
    while (stack.length > 0) {
      const name = stack.pop()!;
      out.push(name);
      for (const c of children.get(name) ?? []) stack.push(c);
    }
    return out;
  };
  const idOf = new Map(rig.mapping.map((m) => [m.rigBone, m.ontologyId]));
  if (!/^(Left|Right)Hand$/.test(idOf.get(handBone) ?? '')) {
    throw new StripDigitsRefusal(`replace fingers refused: "${handBone}" is not mapped as a hand — there is nothing to replace`);
  }
  // A chain is a child whose subtree maps ONLY to digits (at least one): a child that
  // carries any other mapped bone (a hand under a forearm, a socket chain with a
  // mapped prop) is not a finger and stays.
  const chains = (children.get(handBone) ?? []).filter((child) => {
    const ids = subtree(child).map((n) => idOf.get(n)).filter((id): id is string => id !== undefined);
    return ids.length > 0 && ids.every((id) => DIGIT_ID.test(id));
  });
  if (chains.length === 0) {
    throw new StripDigitsRefusal(`replace fingers refused: "${handBone}" has no mapped finger chain — there is nothing to replace; pin it as a fingerless hand`);
  }
  const removed = chains.flatMap(subtree);
  const gone = new Set(removed);
  const next = parseRigDescriptor({
    ...rig,
    bones: rig.bones.filter((b) => !gone.has(b.name)),
    mapping: rig.mapping.filter((m) => !gone.has(m.rigBone)),
    reflectedPairs: rig.reflectedPairs.filter(([a, b]) => !gone.has(a) && !gone.has(b)),
  });
  return { rig: next, removed, chains };
}
